import {Stack, Typography} from "@mui/joy"
import Selectable from "../Selectable"
import {Event} from "../../types/event"
import {isSameEvent} from "../../services/eventSearch"
import {useTranslation} from "react-i18next"

export type EventComponentProps = {
    event: Event
    onSelect?: (event: Event) => void
    /** The row of the fixture being analyzed: shown as picked, and no longer worth clicking */
    selected?: boolean
}

const formatKickOff = (value: string) => {
    const date = new Date(value)

    return isNaN(date.getTime()) ? "—" : date.toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit"
    })
}

/**
 * One match of the prediction api.
 *
 * The played score is shown when there is one, the kick off time otherwise, so the same row serves
 * an upcoming fixture and a finished one.
 */
export const EventComponent = ({event, onSelect, selected}: EventComponentProps) => {
    const {t} = useTranslation()
    const played = event.status_code === "FINISHED" || event.status_code === "LIVE"
    const score = event.score?.final
    // Analyzing the fixture already on screen would only replay the same predictions
    const clickable = !!onSelect && !selected

    return <Selectable selected={selected}
                       disabled={selected}
                       onSelect={clickable ? () => onSelect(event) : undefined}
                       sx={{
                           cursor: clickable ? "pointer" : "default",
                           ...(selected && {pointerEvents: "none", opacity: 0.75})
                       }}>
        <Stack width={"100%"} alignItems={"center"} direction={"row"} justifyContent={"space-between"} gap={1}>
            <Stack width={"32%"}>
                <Typography level={"body-sm"} noWrap>{event.team_home?.name}</Typography>
            </Stack>

            <Stack alignItems={"center"}>
                {played && score
                    ? <Stack direction={"row"} gap={1} alignItems={"center"}>
                        <Typography level={"title-md"}>{score.home}</Typography>
                        <Typography level={"body-xs"}>-</Typography>
                        <Typography level={"title-md"}>{score.away}</Typography>
                    </Stack>
                    : <Typography level={"body-xs"} textColor={"primary.700"}>{t("versus")}</Typography>}
                <Typography level={"body-xs"} noWrap>{formatKickOff(event.start_at)}</Typography>
            </Stack>

            <Stack width={"32%"} alignItems={"flex-end"}>
                <Typography level={"body-sm"} noWrap>{event.team_away?.name}</Typography>
            </Stack>
        </Stack>
    </Selectable>
}

export type EventsProps = {
    events: Event[]
    onSelect?: (event: Event) => void
    /** Fixture whose predictions are on screen, matched by identity rather than by object */
    selectedEvent?: Event
}

const Events = ({events, onSelect, selectedEvent}: EventsProps) => <Stack gap={1}>
    {events.map((event, index) =>
        <EventComponent key={event.id ?? `${event.team_home?.id}-${event.team_away?.id}-${index}`}
                        event={event}
                        onSelect={onSelect}
                        selected={isSameEvent(event, selectedEvent)}/>)}
</Stack>

export default Events
